import { FileProcessStatus } from '@/api';
import FileUploadModal from '@/components/file-upload/file-upload-modal';
import RequiredFormatTooltip from '@/components/file-upload/required-format-tooltip';
import UploadDropZone from '@/components/file-upload/upload-drop-zone';
import type { FileUploadResult } from '@/components/file-upload/use-file-upload';
import { useGetPresignedPutUrl, useGetTemplate } from '@/pages/queries';
import { themeColors } from '@/theme/colors';
import { Icon, TextLink } from '@lego/connect-components-react';
import { Flex } from '@lego/klik-ui';
import { type RefObject, useCallback, useEffect } from 'react';
import {
  COMMENT_MAX_LENGTH,
  TEMPLATE_HEADERS,
  type UploadedPriceRow,
  parseUploadedPriceFile,
} from './helpers';

type SimulationUploadModalProps = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  planningYear: number;
  onClose: () => void;
  onApply: (rows: UploadedPriceRow[]) => void;
};

const SimulationUploadModal = ({
  isOpen,
  triggerRef,
  planningYear,
  onClose,
  onApply,
}: SimulationUploadModalProps) => {
  const {
    data: templateData,
    refetch: refetchTemplate,
    isLoading: isTemplateLoading,
  } = useGetTemplate('simulation_prices');
  const { mutateAsync: getPresignedPutUrl } =
    useGetPresignedPutUrl('simulation_prices');

  useEffect(() => {
    if (!isOpen) return;
    refetchTemplate();
  }, [isOpen, refetchTemplate]);

  const handleTemplateDownload = () => {
    if (templateData?.downloadUrl) {
      window.location.assign(templateData.downloadUrl);
    } else {
      refetchTemplate().then(({ data }) => {
        if (data?.downloadUrl) {
          window.location.assign(data.downloadUrl);
        }
      });
    }
  };

  const handleLocalProcess = useCallback(
    async (file: File): Promise<FileUploadResult> => {
      const result = await parseUploadedPriceFile(file, planningYear);

      if (result.errors.length > 0) {
        const errorMessages = result.errors.map((e) => {
          switch (e.type) {
            case 'file_too_large':
              return `File exceeds maximum size of ${e.maxSizeMb}MB`;
            case 'missing_columns':
              return `Missing required columns: ${e.columns.join(', ')}`;
            case 'empty_file':
              return 'File is empty or has no data';
            case 'no_valid_rows':
              return 'No valid rows found after validation';
          }
        });
        return {
          uploadResult: undefined,
          errorMessage: errorMessages.join('. '),
        };
      }

      onApply(result.rows);

      return {
        uploadResult: {
          status: FileProcessStatus.Completed,
          rowsValid: result.rows.length,
          rowsInvalid: result.warnings.reduce((count, w) => {
            if (w.type === 'invalid_rows' || w.type === 'lp_below_threshold')
              return count + w.rowNumbers.length;
            return count;
          }, 0),
        },
        errorMessage: null,
      };
    },
    [planningYear, onApply],
  );

  return (
    <FileUploadModal
      isOpen={isOpen}
      triggerRef={triggerRef}
      title="Upload — Simulation prices"
      getPresignedPutUrl={getPresignedPutUrl}
      onLocalProcess={handleLocalProcess}
      contentHeight="380px"
      onClose={onClose}
      renderIdle={({ onBrowseClick }) => (
        <>
          <Flex flexDirection="column" gap="8px">
            <span className="body-md-medium" style={{ fontWeight: 700 }}>
              Simulation prices
            </span>
            <span className="body-md-regular">
              Use the provided{' '}
              <TextLink
                href="#"
                variant="emphasis"
                size="medium"
                style={{ textDecoration: 'none' }}
                onClick={(e) => {
                  e.preventDefault();
                  handleTemplateDownload();
                }}
              >
                <Icon
                  name="arrow-down-to-line"
                  aria-label="download template"
                  size="small"
                />
                <span style={{ verticalAlign: 'middle' }}>
                  {isTemplateLoading ? ' downloading...' : ' template'}
                </span>
              </TextLink>{' '}
              to upload simulation prices. Files must match the{' '}
              <RequiredFormatTooltip columns={TEMPLATE_HEADERS} />.
            </span>
            <span
              className="label-sm-regular"
              style={{ color: themeColors.content.subtle }}
            >
              RM values should be entered as percentages (e.g., 15 for 15%).
              Comments are optional (max {COMMENT_MAX_LENGTH} characters) —
              leave blank to keep existing comments. Locked columns are taken
              into account during upload.
            </span>
          </Flex>
          <UploadDropZone onBrowseClick={onBrowseClick} />
        </>
      )}
    />
  );
};

export default SimulationUploadModal;
